import { useModulePermission } from '@sdx/b2o-fe-tools';
import { useEffect, useState } from 'react';

import { isUnauthorizedError } from '../utils/httpErrors';

type PermissionCheckState = {
  isChecking: boolean;
  hasPermission: boolean | null;
  error: string | null;
};

type UseLoginPermissionCheckProps = {
  accessToken: string | null;
  baseUrl: string;
  enabled?: boolean;
};

/**
 * Custom hook to check if user has permission to access the container app
 * Uses the @sdx/b2o-fe-tools permission checking system
 *
 * @param accessToken - User's access token from MSAL
 * @param baseUrl - Base URL for the permissions API
 * @param enabled - Whether to enable the permission check (default: false)
 * @returns Permission check state with loading, permission status, and error
 */
export const useLoginPermissionCheck = ({
  accessToken,
  baseUrl,
  enabled = false,
}: UseLoginPermissionCheckProps): PermissionCheckState => {
  const [permissionState, setPermissionState] = useState<PermissionCheckState>({
    isChecking: false,
    hasPermission: null,
    error: null,
  });

  // Use the manual mode to control when to fetch permissions
  const { permissions, isLoading, hasError, error, refetch, canExecute } = useModulePermission(
    'container-app',
    {
      baseUrl,
      accessToken: accessToken ?? undefined,
    },
    { manual: true },
  );

  useEffect(() => {
    const checkPermission = async () => {
      // Only check if enabled, we have a token, and we can execute the query
      if (!enabled || !accessToken || !canExecute) {
        return;
      }

      // Note: we intentionally do NOT reset hasPermission to null here. On token renewal
      // this effect re-runs (accessToken is a dependency) to recover the check; resetting
      // would drop isFullyAuthenticated mid-session. The derived effect below owns the state.
      try {
        await refetch();
      } catch (err) {
        // Error handling is done through the hasError state below
        console.error('Permission check error:', err);
      }
    };

    void checkPermission();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, canExecute, accessToken]);

  // Update state when permissions data changes
  useEffect(() => {
    if (isLoading) {
      setPermissionState({
        isChecking: true,
        hasPermission: null,
        error: null,
      });
      return;
    }

    if (hasError) {
      // A 401 is a transient token-expiry error, not a permission denial. Don't latch
      // hasPermission=false (which forces a logout in AuthContext) — keep the last-known
      // permission if keepPreviousData still holds it, otherwise leave it unresolved (null).
      // Reserve hasPermission=false for a genuine 403/forbidden or any non-401 failure.
      //
      // Note on null: it does NOT keep the user on the page. With the permission flag on,
      // `hasPermission === null` still leaves isFullyAuthenticated false, so the AppRoutes
      // guard navigates to /login. What null buys is that the redirect is non-destructive —
      // it skips instance.logout() / setAccessToken(null) / SESSION_CLEARED_EVENT, so the
      // MSAL session survives and the check recovers on the renewed token. hasPermission=false
      // tore that session down. The stay-on-page case is keepPreviousData returning the
      // previous true; null is only the no-cache fallback.
      if (isUnauthorizedError(error)) {
        setPermissionState({
          isChecking: false,
          hasPermission: permissions ? permissions.canRead === true : null,
          error: null,
        });
        return;
      }

      setPermissionState({
        isChecking: false,
        hasPermission: false,
        error: error?.message || 'Failed to check permissions',
      });
      return;
    }

    if (permissions) {
      // User has permission if they can read the container-app
      const hasAccess = permissions.canRead === true;
      setPermissionState({
        isChecking: false,
        hasPermission: hasAccess,
        error: hasAccess ? null : 'You do not have permission to access this application',
      });
    }
  }, [isLoading, hasError, error, permissions]);

  return permissionState;
};
